import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { useMemo } from "react";
import { deviceService } from "../services/device.service";
import type { CreateDeviceRequest, Device, ListDevicesParams, UpdateDeviceRequest } from "../types";

// ─── Query Keys ─────────────────────────────────────────────────────
export const deviceKeys = {
	all: ["devices"] as const,
	lists: () => [...deviceKeys.all, "list"] as const,
	list: (params?: ListDevicesParams) => [...deviceKeys.lists(), params] as const,
	details: () => [...deviceKeys.all, "detail"] as const,
	detail: (id: string) => [...deviceKeys.details(), id] as const,
};

// ─── useDevices (list) ───────────────────────────────────────────────
export function useDevices(params?: ListDevicesParams) {
	return useQuery({
		queryKey: deviceKeys.list(params),
		queryFn: () => deviceService.list(params ?? {}),
	});
}

// ─── useDevicesForOrganizations (multi-org merge) ────────────────────
const FETCH_LIMIT_PER_ORG = 50;

export function useDevicesForOrganizations(organizationIds: string[], page: number, limit: number) {
	const queries = useQueries({
		queries: organizationIds.map((organizationId) => ({
			queryKey: deviceKeys.list({ organizationId, page: 1, limit: FETCH_LIMIT_PER_ORG }),
			queryFn: () => deviceService.list({ organizationId, page: 1, limit: FETCH_LIMIT_PER_ORG }),
			enabled: organizationIds.length > 0,
		})),
	});

	const { data, isLoading } = useMemo(() => {
		const loading = queries.some((q) => q.isLoading);
		const allItems: Device[] = queries.flatMap((q) => q.data?.data?.items ?? []);
		const sorted = [...allItems].sort(
			(a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
		);
		const total = sorted.length;
		const paginated = sorted.slice((page - 1) * limit, page * limit);

		return {
			data: {
				data: {
					items: paginated,
					meta: { total, page, limit, totalPages: Math.ceil(total / limit) || 1 },
				},
			},
			isLoading: loading,
		};
	}, [queries, page, limit]);

	return { data, isLoading };
}

// ─── useDevice (getById) ─────────────────────────────────────────────
export function useDevice(id: string) {
	return useQuery({
		queryKey: deviceKeys.detail(id),
		queryFn: () => deviceService.getById(id),
		enabled: !!id,
	});
}

// ─── useCreateDevice ─────────────────────────────────────────────────
export function useCreateDevice() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateDeviceRequest) => deviceService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
			message.success("Qurilma muvaffaqiyatli yaratildi");
		},
	});
}

// ─── useUpdateDevice ─────────────────────────────────────────────────
export function useUpdateDevice() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateDeviceRequest }) =>
			deviceService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
			queryClient.invalidateQueries({ queryKey: deviceKeys.detail(variables.id) });
			message.success("Qurilma muvaffaqiyatli yangilandi");
		},
	});
}

// ─── useDeleteDevice ─────────────────────────────────────────────────
export function useDeleteDevice() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => deviceService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
			message.success("Qurilma muvaffaqiyatli o'chirildi");
		},
	});
}
